import React, {Fragment, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Button, Fade, Container, Form, Modal} from 'react-bootstrap';
import {Redirect} from 'react-router-dom';
import {Formik} from 'formik';
import {useTranslation} from 'react-i18next';
import r from '../images/title_r.svg';
import bg from '../images/img_bg_gradient.jpg';
import CommonMetaTag from './common-meta-tag';
import {ROLES} from '../constants';
import DatePicker from 'react-datepicker';
import GoogleSignIn from './google-sign-in';
import AppleSignIn from './apple-sign-in';
import {FaArrowRight, FaBuilding, FaLock, FaUserPlus} from 'react-icons/fa';
import {isPartnerOwnClubPath} from '../util/partner-club-access';


const Login = ({
  login, googleLogin, googleRegister, appleLogin, appleRegister, appleLinkExisting, location, profile, goto, showRecoveryPasswordModal, isRecoveryPasswordModalVisible, hideRecoveryPasswordModal, recoveryPassword
}) => {
  const {t} = useTranslation();
  const [googleRegistration, setGoogleRegistration] = useState(null);
  const [appleRegistration, setAppleRegistration] = useState(null);
  const [appleRegistrationMode, setAppleRegistrationMode] = useState(null);
  const handleGoogleCredential = async credential => {
    const result = await googleLogin(credential);
    if (result && result.status === 202) setGoogleRegistration(result.json);
  };
  const submitLogin = values => {
    const emailFromDom = (document.getElementById('login-email') || {}).value || '';
    const passwordFromDom = (document.getElementById('login-password') || {}).value || '';
    const credential = {
      email: (values.email || emailFromDom || '').trim(),
      password: values.password || passwordFromDom || ''
    };
    login(credential);
  };
  const handleAppleAuthorization = async authorization => {
    const result = await appleLogin(authorization);
    if (result && result.status === 202) {
      setAppleRegistration(result.json);
      setAppleRegistrationMode(null);
    }
  };
  const nextPath = new URLSearchParams(location.search || '').get('next');
  const mobileSsoFailed = new URLSearchParams(location.search || '').get('mobileSsoError') === '1';
  const safeNextPath = nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//')
    ? nextPath
    : null;
  const signUpPath = nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//')
    ? `/sign-up-player?next=${encodeURIComponent(nextPath)}`
    : '/sign-up-player';
  
  if (profile !== null) {
    const destination = profile.user && profile.user.role === ROLES.admin
      ? '/'
      : profile.user && profile.user.role === ROLES.partner
        ? isPartnerOwnClubPath(profile, safeNextPath) ? safeNextPath : '/partner-fields'
        : safeNextPath || '/';
    return (
      <Redirect to={destination}/>
    );
  }

  return (
    <Fragment>
      <Formik
        initialValues={{email: '', password: ''}}
        onSubmit={submitLogin}
      >
        {({
          handleChange,
          values
        }) => {
          return (
            <Fragment>
              <CommonMetaTag/>
              <Fade in appear style={{transition: 'opacity 0.30s linear'}}>
                <main className="login-skywork-page" style={{backgroundImage: `url(${bg})`}}>
                  <Container className="login-skywork-container">
                    <div className="login-skywork-shell">
                      <section className="login-skywork-story" aria-label={t('MOTTO')}>
                        <div className="login-skywork-brand">
                          <img src={r} alt="" aria-hidden="true"/>
                          <span>Replayer</span>
                        </div>
                        <span className="login-skywork-eyebrow">{t('MOTTO')}</span>
                        <h1>{t('LOGIN_ENTER_REPLAYER')}</h1>
                        <p className="login-skywork-lead">
                          <span className="login-skywork-lead-desktop">{t('LOGIN_SKYWORK_LEAD')}</span>
                          <span className="login-skywork-lead-mobile">{t('LOGIN_SKYWORK_LEAD_MOBILE')}</span>
                        </p>
                      </section>

                      <section className="login-skywork-card">
                        <header className="login-skywork-card-heading">
                          <span className="login-skywork-card-icon"><FaLock aria-hidden="true"/></span>
                          <div>
                            <span className="login-skywork-eyebrow">{t('LOGIN_ACCOUNT_EYEBROW')}</span>
                            <h2>{t('LOGIN_ENTER_REPLAYER')}</h2>
                            <p>{t('LOGIN_CREDENTIALS_CONTINUE')}</p>
                          </div>
                        </header>
                        {mobileSsoFailed ? (
                          <Alert className="login-skywork-alert" variant="warning">
                            {t('LOGIN_MOBILE_SSO_EXPIRED')}
                          </Alert>
                        ) : null}
                        <Form className="login-skywork-form" onSubmit={event => {
                          event.preventDefault();
                          submitLogin(values);
                        }}>
                          <Form.Group>
                            <Form.Label>{t('EMAIL')}</Form.Label>
                            <Form.Control id="login-email" required name="email" placeholder={t('EMAIL')} type="text" autoComplete="email" maxLength="255" value={values.email || ''} onChange={handleChange}/>
                          </Form.Group>
                          <Form.Group>
                            <div className="login-skywork-password-heading">
                              <Form.Label>{t('PASSWORD')}</Form.Label>
                              <button type="button" className="login-skywork-recovery" onClick={showRecoveryPasswordModal}>{t('LOGIN_PASSWORD_RECOVERY')}</button>
                            </div>
                            <Form.Control id="login-password" required name="password" placeholder={t('PASSWORD')} type="password" autoComplete="current-password" value={values.password || ''} onChange={handleChange}/>
                          </Form.Group>
                          <Button type="submit" className="login-skywork-submit">
                            <span>{t('LOGIN_BUTTON')}</span><FaArrowRight aria-hidden="true"/>
                          </Button>
                        </Form>
                        <div className="login-skywork-divider"><span>{t('LOGIN_FAST_ACCESS_TITLE')}</span></div>
                        <p className="login-skywork-fast-access-copy">{t('LOGIN_FAST_ACCESS_DESC')}</p>
                        <div className="login-skywork-social">
                          <GoogleSignIn className="login-skywork-google" onCredential={handleGoogleCredential}/>
                          <AppleSignIn className="login-skywork-apple" label={t('LOGIN_CONTINUE_WITH_APPLE')} onAuthorization={handleAppleAuthorization}/>
                        </div>
                        <div className="login-skywork-account-options">
                          <article className="login-skywork-account-option">
                            <span className="login-skywork-account-option-icon"><FaUserPlus aria-hidden="true"/></span>
                            <strong>{t('LOGIN_PLAYER_TITLE')}</strong>
                            <Button type="button" className="login-skywork-register-button" onClick={goto(signUpPath)}>{t('SIGN_IN_BUTTON')}</Button>
                          </article>
                          <article className="login-skywork-account-option">
                            <span className="login-skywork-account-option-icon"><FaBuilding aria-hidden="true"/></span>
                            <strong>{t('LOGIN_PARTNER_TITLE')}</strong>
                            <a className="login-skywork-info-button" href="https://www.replayer.it/diventa-partner/" target="_blank" rel="noopener noreferrer">
                              <span>{t('LOGIN_MORE_INFO_BUTTON')}</span><FaArrowRight aria-hidden="true"/>
                            </a>
                          </article>
                        </div>
                      </section>
                    </div>
                  </Container>
                </main>
              </Fade>
            </Fragment>
          );
        }}
      </Formik>
      <Modal show={isRecoveryPasswordModalVisible} onHide={hideRecoveryPasswordModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t('LOGIN_PASSWORD_RECOVERY')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{email: null}}
            onSubmit={recoveryPassword}
          >
            {({
              handleSubmit,
              handleChange,
              values
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group>
                    <Form.Label>{t('EMAIL')}</Form.Label>
                    <Form.Control required name="email" placeholder={t('EMAIL')} type="email" value={values.email || ''} onChange={handleChange}/>
                  </Form.Group>
                  <Button type="submit" variant="secondary">{t('LOGIN_PASSWORD_RECOVERY_BUTTON')}</Button>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={hideRecoveryPasswordModal}>
            {t('CLOSE_BUTTON')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!appleRegistration} onHide={() => { setAppleRegistration(null); setAppleRegistrationMode(null); }}>
        <Modal.Header closeButton><Modal.Title>{t('LOGIN_CONTINUE_WITH_APPLE')}</Modal.Title></Modal.Header>
        {!appleRegistrationMode ? (
          <Fragment>
            <Modal.Body>
              <p>{t('LOGIN_APPLE_VERIFIED_EMAIL', {email: appleRegistration ? appleRegistration.email : ''})}</p>
              <p>{t('LOGIN_CHOOSE_HOW_TO_CONTINUE')}</p>
              <Button block className="mb-3" onClick={() => setAppleRegistrationMode('new')}>{t('LOGIN_CREATE_NEW_ACCOUNT')}</Button>
              <Button block variant="outline-primary" onClick={() => setAppleRegistrationMode('existing')}>{t('LOGIN_ALREADY_HAVE_ACCOUNT')}</Button>
            </Modal.Body>
          </Fragment>
        ) : null}
        {appleRegistrationMode === 'existing' ? (
          <Formik
            enableReinitialize
            initialValues={{registrationToken: appleRegistration ? appleRegistration.registrationToken : '', email: '', password: ''}}
            onSubmit={async values => {
              const result = await appleLinkExisting(values);
              if (result && result.status === 200) { setAppleRegistration(null); setAppleRegistrationMode(null); }
            }}
          >
            {({handleSubmit, handleChange, values}) => (
              <Form onSubmit={handleSubmit}>
                <Modal.Body>
                  <p>{t('LOGIN_APPLE_LINK_EXISTING_HELP')}</p>
                  <Form.Group><Form.Label>{t('EMAIL')}</Form.Label><Form.Control required name="email" type="email" value={values.email} onChange={handleChange}/></Form.Group>
                  <Form.Group><Form.Label>{t('PASSWORD')}</Form.Label><Form.Control required name="password" type="password" value={values.password} onChange={handleChange}/></Form.Group>
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setAppleRegistrationMode(null)}>{t('BACK_BUTTON')}</Button><Button type="submit">{t('LOGIN_LINK_AND_LOGIN')}</Button></Modal.Footer>
              </Form>
            )}
          </Formik>
        ) : null}
        {appleRegistrationMode === 'new' ? <Formik
          enableReinitialize
          initialValues={{registrationToken: appleRegistration ? appleRegistration.registrationToken : '', birthDate: null, phoneNumber: '', privacy0: false, privacy1: false, privacy2: false, privacy3: false, privacy4: false}}
          onSubmit={async values => {
            const result = await appleRegister(values);
            if (result && (result.status === 200 || result.status === 201)) setAppleRegistration(null);
          }}
        >
          {({handleSubmit, handleChange, setFieldValue, values}) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <p><strong>{appleRegistration ? appleRegistration.email : ''}</strong></p>
                <Form.Group><Form.Label>{t('BIRTH_DATE')}*</Form.Label><br/><DatePicker required className="form-control" dateFormat="dd/MM/yyyy" maxDate={new Date()} selected={values.birthDate} onChange={value => setFieldValue('birthDate', value)}/></Form.Group>
                <Form.Group><Form.Label>{t('TELEPHONE')}*</Form.Label><Form.Control required name="phoneNumber" type="tel" value={values.phoneNumber} onChange={handleChange}/></Form.Group>
                <Form.Group>
                  <Form.Check required name="privacy0" type="checkbox" label={t('CHECKBOX_SUBSCRIBER')} checked={values.privacy0} onChange={handleChange}/>
                  <Form.Check name="privacy1" type="checkbox" label={t('LABEL_AUTHORIZATION')} checked={values.privacy1} onChange={handleChange}/>
                  <Form.Check name="privacy2" type="checkbox" label={t('STREAMING')} checked={values.privacy2} onChange={handleChange}/>
                  <Form.Check name="privacy3" type="checkbox" label={t('NEWSLETTER')} checked={values.privacy3} onChange={handleChange}/>
                  <Form.Check name="privacy4" type="checkbox" label={t('DATA_TRANSFER')} checked={values.privacy4} onChange={handleChange}/>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer><Button variant="secondary" onClick={() => setAppleRegistrationMode(null)}>{t('BACK_BUTTON')}</Button><Button type="submit">{t('CREATE_ACCOUNT_BUTTON')}</Button></Modal.Footer>
            </Form>
          )}
        </Formik> : null}
      </Modal>
      <Modal show={!!googleRegistration} onHide={() => setGoogleRegistration(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('LOGIN_COMPLETE_REGISTRATION')}</Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={{
            registrationToken: googleRegistration ? googleRegistration.registrationToken : '',
            birthDate: null,
            phoneNumber: '',
            privacy0: false,
            privacy1: false,
            privacy2: false,
            privacy3: false,
            privacy4: false
          }}
          onSubmit={async values => {
            const result = await googleRegister(values);
            if (result && (result.status === 200 || result.status === 201)) setGoogleRegistration(null);
          }}
        >
          {({handleSubmit, handleChange, setFieldValue, values}) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <p><strong>{googleRegistration ? googleRegistration.email : ''}</strong></p>
                <Form.Group>
                  <Form.Label>{t('BIRTH_DATE')}*</Form.Label><br/>
                  <DatePicker required className="form-control" dateFormat="dd/MM/yyyy" maxDate={new Date()} selected={values.birthDate} onChange={value => setFieldValue('birthDate', value)}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('TELEPHONE')}*</Form.Label>
                  <Form.Control required name="phoneNumber" type="tel" value={values.phoneNumber} onChange={handleChange}/>
                </Form.Group>
                <Form.Group>
                  <Form.Check required name="privacy0" type="checkbox" label={t('CHECKBOX_SUBSCRIBER')} checked={values.privacy0} onChange={handleChange}/>
                  <Form.Check name="privacy1" type="checkbox" label={t('LABEL_AUTHORIZATION')} checked={values.privacy1} onChange={handleChange}/>
                  <Form.Check name="privacy2" type="checkbox" label={t('STREAMING')} checked={values.privacy2} onChange={handleChange}/>
                  <Form.Check name="privacy3" type="checkbox" label={t('NEWSLETTER')} checked={values.privacy3} onChange={handleChange}/>
                  <Form.Check name="privacy4" type="checkbox" label={t('DATA_TRANSFER')} checked={values.privacy4} onChange={handleChange}/>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setGoogleRegistration(null)}>{t('CANCEL')}</Button>
                <Button type="submit">{t('CREATE_ACCOUNT_BUTTON')}</Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </Fragment>
  );
};

Login.propTypes = {
  profile: PropTypes.object,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  login: PropTypes.func.isRequired,
  googleLogin: PropTypes.func.isRequired,
  googleRegister: PropTypes.func.isRequired,
  appleLogin: PropTypes.func.isRequired,
  appleRegister: PropTypes.func.isRequired,
  appleLinkExisting: PropTypes.func.isRequired,
  goto: PropTypes.func.isRequired,
  showRecoveryPasswordModal: PropTypes.func.isRequired,
  hideRecoveryPasswordModal: PropTypes.func.isRequired,
  isRecoveryPasswordModalVisible: PropTypes.bool,
  recoveryPassword: PropTypes.func.isRequired
};

Login.defaultProps = {
  profile: null,
  isRecoveryPasswordModalVisible: false
};

export default Login;
